import { EVENT_STATUS_V2 } from '../../../constants/service/app/eventConstants';
import { EventFormData, SectionTab, ProductItem, TagItem } from './types';
import { Attachment } from '../../../components/input/ImageAndVideoUploadV2/types';
import {
  EventsCreateRequestBodyDto,
  EventsFindOneResponseBodyDto,
  EventsFindOneResponseBodyEventItemDto,
  EventsFindOneResponseBodyEventSectionItemDto,
  EventsUpdateRequestBodyDto,
} from '../../../types/api/events';
import { EventDetailProductItem, EventDetailResponse } from '../../../types/query/event/detail';
import { EventSectionClickLayer } from '../../../types/query/event';
import { setDateFormat } from '../../../lib/moment';
import { DATE_FORMAT } from '../../../constants/service/common/utilConstants';
import { PRODUCT_SORT_TYPE } from '../../../constants/service/app/productConstants';
import { normalizeLinkUrl } from '../../../lib/utils/urlUtils';

const convertProduct = (
  param: EventsFindOneResponseBodyEventItemDto | EventsFindOneResponseBodyEventSectionItemDto,
): EventDetailProductItem => {
  const { product, store, productAttachments, productCategories, ...rest } = param;
  return {
    ...rest,
    product: {
      ...product,
      discountStartDate:
        product.discountStartDate && setDateFormat(product.discountStartDate, DATE_FORMAT.FULL_DASH_FORMAT),
      discountEndDate: product.discountEndDate && setDateFormat(product.discountEndDate, DATE_FORMAT.FULL_DASH_FORMAT),
      createDate: setDateFormat(product.createDate, DATE_FORMAT.FULL_DASH_FORMAT),
    },
    brand: store,
    productAttachmentList: productAttachments,
    productCategoryList: productCategories,
  };
};

export const convertGetEventDetailResponseData = ({ event }: EventsFindOneResponseBodyDto): EventDetailResponse => {
  return {
    id: event.id,
    mainTitle: event.mainTitle,
    status: event.status,
    period: {
      startDate: setDateFormat(event.startDate, DATE_FORMAT.FULL_DASH_FORMAT),
      endDate: setDateFormat(event.endDate, DATE_FORMAT.FULL_DASH_FORMAT),
    },
    contents: event.contents,
    memo: event.memo,
    itemDefaultSortType: event.itemDefaultSortType,
    isItemFilterExpose: event.isItemFilterExpose,
    eventAttachmentList: event.eventAttachments,
    eventSectionList: event.eventSections.map(
      ({ eventSectionAttachments, eventSectionClickLayers, eventSectionItems, ...eventSectionRes }) => ({
        ...eventSectionRes,
        eventSectionAttachmentList: eventSectionAttachments,
        // 기존 DB 에 누적된 공백·비가시 문자를 폼 로딩 시점에 정리해, 사용자가 만지지 않아도 다음 저장 시 클린해진다
        eventSectionClickLayerList: eventSectionClickLayers?.map((layer) => ({
          ...layer,
          url: normalizeLinkUrl(layer.url),
        })),
        eventSectionProductList: eventSectionItems.map(convertProduct),
      }),
    ),
    eventProductList: event.eventItems.map(convertProduct),
    eventTagList: event.eventTagLinks.map(({ tag, tagAttachments, tagGroups, ...eventTagLinkRes }) => ({
      ...eventTagLinkRes,
      tag: {
        ...tag,
        createDate: setDateFormat(tag.createDate, DATE_FORMAT.FULL_DASH_FORMAT),
      },
      tagAttachmentList: tagAttachments,
      tagGroupList: tagGroups,
    })),
    eventColorSet: event.eventColorSet,
  };
};

export const convertSubmitRequestData = (data: EventFormData, isEventColorSet: boolean) => {
  let startDate;
  let endDate;
  if (data.status !== EVENT_STATUS_V2.UNLIMITED) {
    startDate = data.period?.startDate;
    endDate = data.period?.endDate;
  }
  const eventSections = data.sectionTabList.map((tab: SectionTab) => ({
    type: tab.type,
    transitionDelay: data[`${tab.name}-transitionDelay`],
    isInfinity: data[`${tab.name}-isInfinity`],
    title: data[`${tab.name}-title`]?.trim(),
    tabTitle: data[`${tab.name}-tabTitle`]?.trim(),
    eventSectionAttachments: data[`${tab.name}-attachments`]?.map((item: Attachment) => ({
      ...item,
      type: item.type === 'video' ? item.type : 'image',
    })),
    eventSectionClickLayers: data[`${tab.name}-clickLayers`]?.map((layer: EventSectionClickLayer) => ({
      ...layer,
      url: normalizeLinkUrl(layer.url),
    })),
    eventSectionItems: data[`${tab.name}-product-items`]?.map((item: ProductItem) => ({
      id: item.eventId,
      productId: item.id,
    })),
  }));

  return {
    mainTitle: data.mainTitle?.trim(),
    contents: data.contents?.trim(),
    memo: data.memo?.trim(),
    // todo: 정렬 순서 변경 작업은 추후에 진행할 예정이라 default 값을 임시 설정
    itemDefaultSortType: PRODUCT_SORT_TYPE.RECOMMENDATION,
    isItemFilterExpose: data.isItemFilterExpose,
    status: data.status,
    startDate: startDate,
    endDate: endDate,
    eventAttachments: data.eventAttachment?.map((item: Attachment) => ({ ...item, type: 'search' })),
    eventSections,
    eventItems:
      data['event-product-items']?.map((item: ProductItem) => ({
        id: item.eventId,
        productId: item.id,
      })) ?? [],
    eventTagLinks:
      data['event-tag-items']?.map((item: TagItem) => ({
        id: item.eventId,
        tagId: item.id,
      })) ?? [],
    eventColorSet: isEventColorSet
      ? {
          isDefaultColor: data.isDefaultColor,
          sectionTabBackgroundColor: data.sectionTabBackgroundColor,
          sectionTabActiveColor: data.sectionTabActiveColor,
          sectionTabInactiveColor: data.sectionTabInactiveColor,
          id: data.colorSetId,
        }
      : null,
  } as EventsCreateRequestBodyDto | EventsUpdateRequestBodyDto;
};
